/**
 * ImageCropperModal Component
 *
 * Modal for cropping images with pan and zoom using react-easy-crop.
 *
 * - Local files (imageFile): blob URL directly, no network call
 * - Server images (imageUrl): pre-fetched through backend proxy on open,
 *   converted to blob URL. Save is always instant — proxy work is done upfront.
 */

import { X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import Cropper from "react-easy-crop"
import { UploadLinkIcon } from "@/components/icons/index"
import { Button } from "@/components/ui/button"
import { THUMBNAIL_LIMITS } from "@/constants/media"

interface ImageCropperModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  imageFile: File | null
  imageUrl?: string | null
  onCropComplete: (croppedFile: File) => void
  title?: string
  description?: string
}

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
}

interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

function toProxyUrl(httpUrl: string): string {
  try {
    const url = new URL(httpUrl)
    const segments = url.pathname.split("/").filter(Boolean)
    if (segments.length < 2) return httpUrl
    const key = segments.slice(1).join("/")
    const apiUrl = import.meta.env.VITE_API_URL
    const proxyUrl = `${apiUrl}/api/utils/image-data?key=${encodeURIComponent(key)}`
    return proxyUrl
  } catch {
    return httpUrl
  }
}

export function ImageCropperModal({
  open,
  onOpenChange,
  imageFile,
  imageUrl,
  onCropComplete,
  title = "Crop your images",
  description = "Refine how each image appears in your ritual. Drag to reposition and zoom to crop.",
}: ImageCropperModalProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [isProcessing, setIsProcessing] = useState(false)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setImageSrc(null)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setCroppedAreaPixels(null)
      setIsLoading(false)
      return
    }

    if (imageFile) {
      // Local file — blob URL, no network
      setImageSrc(URL.createObjectURL(imageFile))
      return
    }

    if (imageUrl) {
      // Blob URL — use directly, no proxy needed
      if (imageUrl.startsWith("blob:")) {
        setImageSrc(imageUrl)
        return
      }
      // Server image — fetch through proxy to get same-origin blob
      setIsLoading(true)
      const proxyUrl = toProxyUrl(imageUrl)
      fetch(proxyUrl)
        .then(res => {
          if (!res.ok) throw new Error(`Proxy returned ${res.status}`)
          return res.blob()
        })
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob)
          setImageSrc(blobUrl)
        })
        .catch(() => setImageSrc(null))
        .finally(() => setIsLoading(false))
    }
  }, [open, imageFile, imageUrl])

  const handleCropComplete = useCallback((_: CropArea, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleReplaceImage = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        if (imageSrc) URL.revokeObjectURL(imageSrc)
        setImageSrc(URL.createObjectURL(file))
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setCroppedAreaPixels(null)
      }
      if (fileInputRef.current) fileInputRef.current.value = ""
    },
    [imageSrc],
  )

  const createCroppedImage = useCallback(
    async (area: CropArea) => {
      if (!imageSrc) return null

      const mimeType = imageFile?.type || "image/jpeg"
      const fileName = imageFile?.name || "cropped-image"

      const img = new Image()

      const result = await new Promise<File>((resolve, reject) => {
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas")
            const ctx = canvas.getContext("2d")
            if (!ctx) throw new Error("No 2d context")

            canvas.width = 800
            canvas.height = 600

            ctx.drawImage(
              img,
              area.x,
              area.y,
              area.width,
              area.height,
              0,
              0,
              canvas.width,
              canvas.height,
            )

            canvas.toBlob(
              blob => {
                if (!blob) {
                  reject(new Error("Canvas is empty"))
                  return
                }
                const ext = MIME_TO_EXT[mimeType] || "jpg"
                resolve(
                  new File([blob], fileName.replace(/\.[^.]+$/, `-cropped.${ext}`), {
                    type: mimeType,
                  }),
                )
              },
              mimeType,
              0.95,
            )
          } catch (err) {
            reject(err)
          }
        }
        img.onerror = () => reject(new Error("Failed to load image"))
        img.src = imageSrc
      })

      return result
    },
    [imageSrc, imageFile],
  )

  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels) return
    setIsProcessing(true)
    try {
      const croppedFile = await createCroppedImage(croppedAreaPixels)
      if (croppedFile) {
        onCropComplete(croppedFile)
        onOpenChange(false)
      }
    } catch (error) {
      console.error("Crop failed:", error)
    } finally {
      setIsProcessing(false)
    }
  }, [croppedAreaPixels, createCroppedImage, onCropComplete, onOpenChange])

  const handleClose = useCallback(() => {
    if (imageSrc) URL.revokeObjectURL(imageSrc)
    setImageSrc(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    onOpenChange(false)
  }, [imageSrc, onOpenChange])

  const onZoomChange = useCallback((z: number) => setZoom(z), [])
  const onCropChange = useCallback((c: { x: number; y: number }) => setCrop(c), [])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.9)]" onClick={handleClose} />
      <div className="relative z-10 flex w-full max-w-[600px] flex-col rounded-tl-2xl rounded-br-2xl border bg-[#1C1C1E] p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button
            onClick={handleClose}
            className="cursor-pointer rounded-full border border-input p-2 hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <p className="mb-4 text-[13px] leading-[20px] text-[#A1A1A6]">{description}</p>

        <div className="relative mb-4 h-[300px] w-full overflow-hidden rounded-lg bg-black">
          {isLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            </div>
          ) : imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={1}
              maxZoom={3}
              showGrid={false}
              cropShape="rect"
              objectFit="cover"
              onCropChange={onCropChange}
              onCropComplete={handleCropComplete}
              onZoomChange={onZoomChange}
              style={{ containerStyle: { width: "100%", height: "100%" } }}
            />
          ) : null}
        </div>

        <div className="mb-6 flex items-center gap-4 mt-0">
          <input
            type="range"
            min="1"
            max="3"
            step="0.1"
            value={zoom}
            onChange={e => onZoomChange(parseFloat(e.target.value))}
            className="flex-1 h-1 bg-[#38383A] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-terracotta"
          />
        </div>

        <div className="mb-6 flex flex-row gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={THUMBNAIL_LIMITS.ALLOWED_TYPES.join(",")}
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={handleReplaceImage}
            className="flex-1 flex items-center justify-center gap-2 text-sm text-brand-terracotta hover:text-brand-terracotta/80 transition-colors uppercase"
          >
            <UploadLinkIcon className="size-4" />
            Upload New Image
          </Button>
          <Button
            onClick={handleApply}
            disabled={isProcessing || !croppedAreaPixels || isLoading}
            className="flex-1 uppercase"
          >
            {isProcessing ? "Processing..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  )
}
